import { memo, type ReactNode } from "react";
import type { IconAsset, IconPlate, Space } from "@desk/contracts";
import { useHierarchy } from "@/data/hooks";
import { cn } from "@/lib/utils";
import { SpaceGlyph } from "./icons";
import { SymbolIcon, symbolGlyph } from "./symbol-icon";

const PLATE: Record<IconPlate, string> = {
  none: "",
  light: "bg-[#F7F5F2]",
  dark: "bg-[#252422]",
};

const dataUri = (svg: string) => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;

/**
 * An imported icon. The server already padded it into a square and resolved its
 * colors per theme; the light and dark renditions swap with the app theme, and a
 * neutral tile appears only when the mark would be too faint on that theme.
 */
export const ContainerIcon = memo(function ContainerIcon({ asset, className }: { asset: IconAsset; className?: string }) {
  const img = (variant: IconAsset["light"], themeClass: string) => (
    <img
      src={dataUri(variant.svg)}
      alt=""
      draggable={false}
      className={cn("size-full select-none rounded-[22%] object-contain", PLATE[variant.plate], themeClass)}
    />
  );
  return (
    <span aria-hidden data-icon-asset={asset.id} className={cn("inline-flex size-[18px] shrink-0", className)}>
      {img(asset.light, "dark:hidden")}
      {img(asset.dark, "hidden dark:block")}
    </span>
  );
});

/** What an area or space shows: its logo, else its symbol, else `fallback`. */
type IconFields = { iconAssetId?: string | null; icon?: string | null; iconColor?: string | null };

export function ContainerGlyph({ of, className, fallback = null }: { of: IconFields | null | undefined; className?: string; fallback?: ReactNode }) {
  const asset = useHierarchy().asset(of?.iconAssetId);
  if (asset) return <ContainerIcon asset={asset} className={className} />;
  if (of?.icon && symbolGlyph(of.icon)) return <SymbolIcon sf={of.icon} color={of.iconColor} className={className} />;
  return <>{fallback}</>;
}

/** A space's logo or symbol, or its letter glyph when it has neither. */
export function SpaceIcon({ space, name, className }: { space?: (IconFields & Pick<Space, "name">) | null; name?: string; className?: string }) {
  return <ContainerGlyph of={space} className={className} fallback={<SpaceGlyph name={space?.name ?? name ?? "?"} className={className} />} />;
}

/** The icon of the space a task is filed in, else its area's; nothing for Inbox. */
export function LocationIcon({ task, className }: { task: { spaceId: string | null; areaId: string | null }; className?: string }) {
  const h = useHierarchy();
  const space = h.space(task.spaceId);
  if (space) return <SpaceIcon space={space} className={className} />;
  const area = h.area(task.areaId);
  if (area) return <ContainerGlyph of={area} className={className} fallback={<SpaceGlyph name={area.name} className={className} />} />;
  return null;
}
